/**
 * ExtendedProfilePage — страница управления расширенными данными
 *
 * Табы: Addresses | Phones | Children | Pets | Companies
 * Каждый таб: список + кнопка "Добавить" + форма
 * API: GET/POST/DELETE /api/profile/{type}
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { handleApiError } from '../../utils/apiErrors';
import { logger } from '../../utils/logger';

// ============================================================
// Types
// ============================================================

type ExtendedTabId = 'addresses' | 'phones' | 'children' | 'pets' | 'companies';

interface AddressItem {
  id: string;
  label: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  timezone?: string;
  isPrimary?: boolean;
}

interface PhoneItem {
  id: string;
  label: string;
  number: string;
  isPrimary?: boolean;
}

interface ChildItem {
  id: string;
  name: string;
  birthDate: string;
  birthTime?: string;
  birthPlace?: string;
  gender?: string;
}

interface PetItem {
  id: string;
  name: string;
  species: string;
  birthDate?: string;
  notes?: string;
}

interface CompanyItem {
  id: string;
  name: string;
  role: string;
  startDate?: string;
  endDate?: string;
  city?: string;
}

type ItemMap = {
  addresses: AddressItem;
  phones: PhoneItem;
  children: ChildItem;
  pets: PetItem;
  companies: CompanyItem;
};

type ItemOf<T extends ExtendedTabId> = ItemMap[T];

// ============================================================
// Styles — тёмная тема, gold акценты (как в ProfilePage)
// ============================================================

const colors = {
  bg: '#0a0814',
  bgCard: 'rgba(13, 20, 40, 0.85)',
  border: 'rgba(212, 175, 55, 0.12)',
  gold: '#d4af37',
  goldLight: '#f4e4a6',
  text: '#f0f0f0',
  textMuted: '#b0b0b0',
  error: '#FF5252',
  success: '#4CAF50',
};

const radius = { sm: '8px', md: '12px', lg: '24px' };
const spacing = { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' };
const fontSize = { sm: '13px', md: '14px', lg: '16px', xl: '18px', title: '22px', heading: '28px' };

const pageStyle: React.CSSProperties = {
  minHeight: '80vh',
  padding: '20px',
  maxWidth: '900px',
  margin: '0 auto',
};

const cardStyle: React.CSSProperties = {
  background: colors.bgCard,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  padding: spacing.xl,
  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#ffffff',
  fontWeight: 500,
  marginBottom: '6px',
  fontSize: fontSize.md,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: 'rgba(255, 255, 255, 0.06)',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  color: '#ffffff',
  fontSize: fontSize.md,
  boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.goldLight} 100%)`,
  border: 'none',
  padding: '10px 20px',
  borderRadius: radius.md,
  color: colors.bg,
  fontSize: fontSize.md,
  fontWeight: 'bold',
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  background: 'rgba(212, 175, 55, 0.1)',
  border: `1px solid ${colors.border}`,
  padding: '8px 16px',
  borderRadius: radius.sm,
  color: colors.gold,
  fontSize: fontSize.sm,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  background: 'rgba(220, 53, 69, 0.1)',
  border: '1px solid rgba(220, 53, 69, 0.3)',
  padding: '6px 12px',
  borderRadius: radius.sm,
  color: colors.error,
  fontSize: fontSize.sm,
  cursor: 'pointer',
};

const backBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  padding: '8px 16px',
  color: colors.gold,
  fontSize: fontSize.sm,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const itemCardStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.03)',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: spacing.md,
  marginBottom: spacing.sm,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const tabBtnStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  padding: '10px 16px',
  background: active ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
  border: 'none',
  borderBottom: active ? `2px solid ${colors.gold}` : '2px solid transparent',
  color: active ? colors.gold : colors.textMuted,
  fontSize: fontSize.md,
  cursor: 'pointer',
  transition: 'all 0.3s',
  whiteSpace: 'nowrap',
});

// ============================================================
// Tab config
// ============================================================

const TABS: { id: ExtendedTabId; icon: string; label: string }[] = [
  { id: 'addresses', icon: '📍', label: 'Адреса' },
  { id: 'phones', icon: '📞', label: 'Телефоны' },
  { id: 'children', icon: '👶', label: 'Дети' },
  { id: 'pets', icon: '🐾', label: 'Питомцы' },
  { id: 'companies', icon: '🏢', label: 'Компании' },
];

// ============================================================
// Form fields per tab
// ============================================================

interface FieldDef {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'time' | 'number';
  placeholder?: string;
  required?: boolean;
}

const FORM_FIELDS: Record<ExtendedTabId, FieldDef[]> = {
  addresses: [
    { key: 'label', label: 'Название', placeholder: 'Дом, Работа', required: true },
    { key: 'city', label: 'Город', required: true },
    { key: 'country', label: 'Страна' },
    { key: 'latitude', label: 'Широта', type: 'number', required: true },
    { key: 'longitude', label: 'Долгота', type: 'number', required: true },
    { key: 'timezone', label: 'Часовой пояс', placeholder: 'Europe/Moscow' },
  ],
  phones: [
    { key: 'label', label: 'Название', placeholder: 'Мобильный, Домашний', required: true },
    { key: 'number', label: 'Номер', placeholder: '+7 (999) 123-45-67', required: true },
  ],
  children: [
    { key: 'name', label: 'Имя', required: true },
    { key: 'birthDate', label: 'Дата рождения', type: 'date', required: true },
    { key: 'birthTime', label: 'Время рождения', type: 'time' },
    { key: 'birthPlace', label: 'Место рождения' },
    { key: 'gender', label: 'Пол', placeholder: 'male / female' },
  ],
  pets: [
    { key: 'name', label: 'Кличка', required: true },
    { key: 'species', label: 'Вид', placeholder: 'Кошка, Собака', required: true },
    { key: 'birthDate', label: 'Дата рождения', type: 'date' },
    { key: 'notes', label: 'Заметки' },
  ],
  companies: [
    { key: 'name', label: 'Название компании', required: true },
    { key: 'role', label: 'Должность', required: true },
    { key: 'startDate', label: 'Дата начала', type: 'date' },
    { key: 'endDate', label: 'Дата окончания', type: 'date' },
    { key: 'city', label: 'Город' },
  ],
};

// ============================================================
// Component
// ============================================================

const ExtendedProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [activeTab, setActiveTab] = useState<ExtendedTabId>('addresses');
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Load items when tab changes
  useEffect(() => {
    loadItems();
  }, [activeTab]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Record<string, unknown>[]>(`/profile/${activeTab}`);
      if (res.success && Array.isArray(res.data)) {
        setItems(res.data);
      }
    } catch (err) {
      const msg = handleApiError(err, 'Не удалось загрузить данные');
      setError(msg);
      logger.error(`loadItems(${activeTab}) error`, err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({});
    setShowForm(true);
    setError(null);
    setMessage(null);
  };

  const openEditForm = (item: Record<string, unknown>) => {
    setEditingId((item.id as string) || null);
    const fields = FORM_FIELDS[activeTab];
    const data: Record<string, string> = {};
    for (const f of fields) {
      data[f.key] = String(item[f.key] ?? '');
    }
    setFormData(data);
    setShowForm(true);
    setError(null);
    setMessage(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (editingId) {
        const res = await api.put<Record<string, unknown>>(`/profile/${activeTab}/${editingId}`, formData);
        if (res.success) {
          setMessage('Сохранено');
          setShowForm(false);
          loadItems();
        } else {
          setError(res.error || 'Ошибка сохранения');
        }
      } else {
        const res = await api.post<Record<string, unknown>>(`/profile/${activeTab}`, formData);
        if (res.success) {
          setMessage('Добавлено');
          setShowForm(false);
          loadItems();
        } else {
          setError(res.error || 'Ошибка сохранения');
        }
      }
    } catch (err) {
      setError(handleApiError(err, 'Ошибка соединения'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить эту запись?')) return;
    try {
      const res = await api.delete(`/profile/${activeTab}/${id}`);
      if (res.success) {
        setItems((prev) => prev.filter((item) => (item as Record<string, unknown>).id !== id));
        setMessage('Удалено');
      } else {
        setError(res.error || 'Не удалось удалить');
      }
    } catch (err) {
      setError(handleApiError(err, 'Ошибка соединения'));
    }
  };

  const isErrorMsg = (msg: string) =>
    msg.includes('Ошибка') || msg.includes('не удалось') || msg.includes('Неверный');

  // ============================================================
  // Render: item list
  // ============================================================

  const renderItem = (item: Record<string, unknown>) => {
    const fields = FORM_FIELDS[activeTab];
    const primaryLabel = (item.label as string) || (item.name as string) || '';
    const secondaryParts: string[] = [];

    for (const f of fields) {
      if (f.key === 'label' || f.key === 'name') continue;
      const val = item[f.key];
      if (val !== undefined && val !== null && val !== '') {
        secondaryParts.push(`${f.label}: ${val}`);
      }
    }

    return (
      <div key={String(item.id)} style={itemCardStyle}>
        <div style={{ flex: 1 }}>
          <div style={{ color: colors.gold, fontSize: fontSize.lg, fontWeight: 'bold', marginBottom: '4px' }}>
            {primaryLabel}
          </div>
          {secondaryParts.length > 0 && (
            <div style={{ color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 1.5 }}>
              {secondaryParts.slice(0, 3).join(' • ')}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: spacing.sm, marginLeft: spacing.md }}>
          <button onClick={() => openEditForm(item)} style={btnSecondary}>
            Изменить
          </button>
          <button onClick={() => handleDelete(String(item.id))} style={btnDanger}>
            Удалить
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // Render: form
  // ============================================================

  const renderForm = () => {
    const fields = FORM_FIELDS[activeTab];

    return (
      <div style={{ ...cardStyle, marginTop: spacing.md }}>
        <h3 style={{ color: '#f0f0f0', fontSize: fontSize.xl, marginBottom: spacing.lg }}>
          {editingId ? 'Редактировать' : 'Добавить'}
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg, marginBottom: spacing.lg }}>
          {fields.map((f) => (
            <div key={f.key}>
              <label style={labelStyle}>
                {f.label} {f.required && <span style={{ color: colors.error }}>*</span>}
              </label>
              <input
                type={f.type || 'text'}
                value={formData[f.key] || ''}
                placeholder={f.placeholder}
                onChange={(e) => setFormData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                style={inputStyle}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: spacing.sm }}>
          <button onClick={handleSave} disabled={saving} style={{
            ...btnPrimary,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={() => { setShowForm(false); setEditingId(null); }} style={btnSecondary}>
            Отмена
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div style={{ ...pageStyle, padding: isMobile ? '16px 12px' : '20px', position: 'relative' }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      {/* Back button */}
      <div style={{ marginBottom: spacing.lg }}>
        <button onClick={() => navigate('/profile')} style={backBtnStyle}>
          ← Назад к профилю
        </button>
      </div>

      <h1 style={{
        fontSize: isMobile ? '22px' : fontSize.heading,
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
        backgroundSize: '200% auto',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: spacing.sm,
      }}>
        Расширенные данные
      </h1>

      <p style={{ color: '#b0b0b0', fontSize: isMobile ? '13px' : fontSize.md, marginBottom: spacing.xl }}>
        Управляйте адресами, телефонами, детьми, питомцами и компаниями
      </p>

      {/* Message banners */}
      {message && (
        <div style={{
          background: isErrorMsg(message) ? 'rgba(220, 53, 69, 0.2)' : 'rgba(40, 167, 69, 0.2)',
          border: `1px solid ${isErrorMsg(message) ? colors.error : colors.success}`,
          color: isErrorMsg(message) ? colors.error : colors.success,
          padding: spacing.md,
          borderRadius: radius.md,
          marginBottom: spacing.md,
          fontSize: fontSize.md,
        }}>
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: spacing.xs,
        marginBottom: spacing.xl,
        borderBottom: `1px solid ${colors.border}`,
        overflowX: 'auto',
      }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setShowForm(false); setEditingId(null); }}
            style={tabBtnStyle(activeTab === tab.id)}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Card */}
      <div style={{ ...cardStyle, padding: isMobile ? '20px 16px' : spacing.xl, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
        {/* Header with Add button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg }}>
          <h2 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : fontSize.title, margin: 0 }}>
            {TABS.find((t) => t.id === activeTab)?.label}
          </h2>
          {!showForm && (
            <button onClick={openCreateForm} style={btnPrimary}>
              + Добавить
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.textMuted }}>
            Загрузка...
          </div>
        )}

        {/* Error */}
        {error && !showForm && (
          <div style={{
            textAlign: 'center',
            padding: spacing.xl,
            color: colors.error,
          }}>
            {error}
            <button onClick={loadItems} style={{ ...btnSecondary, marginLeft: spacing.sm }}>
              Повторить
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items.length === 0 && !showForm && (
          <div style={{
            textAlign: 'center',
            padding: spacing.xl,
            color: colors.textMuted,
          }}>
            <div style={{ fontSize: '48px', marginBottom: spacing.md }}>
              {TABS.find((t) => t.id === activeTab)?.icon}
            </div>
            <p>Пока нет записей. Нажмите «Добавить» чтобы создать первую.</p>
          </div>
        )}

        {/* Item list */}
        {!loading && !showForm && items.length > 0 && (
          <div>
            {items.map((item) => renderItem(item as Record<string, unknown>))}
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && renderForm()}
    </div>
  );
};

export default ExtendedProfilePage;
